import { match } from 'ts-pattern';

import DropGridItem from 'components/DropGridItem';
import EditionGridItem from 'components/EditionGridItem';
import FrameGrid from 'components/FrameGrid';
import NftGridItem from 'components/NftGridItem';
import Modal from 'components/base/Modal';
import Text from 'components/base/Text';
import { EditorMachineContext } from 'machines/editor';

import useModalVisibility from 'hooks/use-modal-visibility';
import { mapDropSaleConfigurationToDropSale } from 'schemas/parse/drop-sale';
import { mapApiMediaToPreviewMediaAsset } from 'utils/media-preview';
import { MomentInventoryItem } from 'utils/story/story';

import { ModalOptions } from 'types/modal';

const MODAL_KEY = 'STORY_NFT';
const MAX_COLUMNS = 3;

type StoryNftModalProps = {
  itemIdsInStory: string[];
  onSelectItem: (nft: MomentInventoryItem) => void;
};

type StoryNftModalOptions = ModalOptions<typeof MODAL_KEY>;

export default function StoryNftModal(props: StoryNftModalProps) {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && (
            <StoryNftModalWindow
              onOpenChange={modal.onOpenChange}
              {...props}
              {...modal.config}
            />
          )}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function StoryNftModalWindow(
  props: StoryNftModalProps &
    StoryNftModalOptions & {
      onOpenChange: () => void;
    }
) {
  const momentItems = EditorMachineContext.useSelector(
    (state) => state.context.momentItems
  );

  const filteredMomentsItems = momentItems.filter(
    (item) => !props.itemIdsInStory.includes(item.id)
  );

  const getBody = () => {
    return (
      <FrameGrid.Provider value={{ type: 'SELECTABLE' }}>
        <FrameGrid.Root
          maxColumns={MAX_COLUMNS}
          css={{
            paddingBottom: '$6',
          }}
        >
          {filteredMomentsItems.map((item) => {
            return match(item)
              .with({ type: 'MARKET_NFT' }, (item) => {
                const media = item.media
                  ? mapApiMediaToPreviewMediaAsset(item.media)
                  : null;

                const optimizedMedia = media
                  ? NftGridItem.optimizeMedia(media, { maxColumns: 2 })
                  : null;

                return (
                  <NftGridItem.Selectable
                    key={item.id}
                    nft={item}
                    media={optimizedMedia}
                    onClick={() => {
                      props.onSelectItem(item);
                      props.onOpenChange();
                    }}
                  />
                );
              })
              .with({ type: 'SOLD_NFT' }, (item) => {
                const media = item.media
                  ? mapApiMediaToPreviewMediaAsset(item.media)
                  : null;

                const optimizedMedia = media
                  ? NftGridItem.optimizeMedia(media, { maxColumns: 2 })
                  : null;

                return (
                  <NftGridItem.Selectable
                    key={item.id}
                    nft={item}
                    media={optimizedMedia}
                    onClick={() => {
                      props.onSelectItem(item);
                      props.onOpenChange();
                    }}
                  />
                );
              })
              .with({ type: 'DROP_COLLECTION' }, (item) => (
                <DropGridItem.Selectable
                  type="SINGLE"
                  key={item.id}
                  drop={item}
                  sale={mapDropSaleConfigurationToDropSale(
                    item.saleConfiguration
                  )}
                  onClick={() => {
                    props.onSelectItem(item);
                    props.onOpenChange();
                  }}
                />
              ))
              .with({ type: 'EDITION_COLLECTION' }, (item) => {
                return (
                  <EditionGridItem.Selectable
                    version="legacy"
                    type="SINGLE"
                    key={item.id}
                    edition={item}
                    sale={item.sale}
                    onClick={() => {
                      props.onSelectItem(item);
                      props.onOpenChange();
                    }}
                  />
                );
              })
              .exhaustive();
          })}
        </FrameGrid.Root>
      </FrameGrid.Provider>
    );
  };

  return (
    <Modal.Window
      maxWidth={960}
      header={
        <Modal.Header>
          <Text
            weight="semibold"
            size={2}
            css={{ textAlign: 'center', width: '100%' }}
          >
            Select work
          </Text>
        </Modal.Header>
      }
    >
      {getBody()}
    </Modal.Window>
  );
}
